import React from 'react'
import PropTypes from 'prop-types'
import { format, isValid } from 'date-fns'
import { Stack, Divider, Flex, Button, useControllableState } from '@chakra-ui/react'
import { useDatepicker, START_DATE, END_DATE } from '@datepicker-react/hooks'

import { DatePickerCoreProvider, Month, Navigation, SelectedDate, NAVIGATION_GUIDE } from '../date-picker-core'
import { TimePicker } from '../time-picker'

const defaultLocales = {
  Apply: 'Apply',
  Clear: 'Clear',
  StartDate: 'Start Date',
  EndDate: 'End Date',
  StartTime: 'Start Time',
  EndTime: 'End Time',
}
const defaultFormatAdapter = {
  firstDayOfWeek: 0,
  input: (date) => format(date, 'eee, MMMM d, yyyy'),
  timePicker: {
    input: (time) => format(time, 'MMM d, h:mm a'),
    label: (time) => format(time, 'h:mm a'),
  },
  month: (date) => format(date, 'MMMM, yyyy'),
  weekday: (date) => format(date, 'eee'),
  day: (date) => format(date, 'd'),
}

export const DateRangePicker = ({
  min,
  max,
  withTime,
  colorScheme,
  disabledDates,
  defaultValue,
  value: valueProp,
  onChange,
  onConfirm: onConfirmProp,
  formatAdapter: formatAdapterProp,
  locales: localesProp,
  components,
  propsOf,
}) => {
  const [value, setValue] = useControllableState({ defaultValue, value: valueProp, onChange })
  const [focusedInput, setFocusedInput] = React.useState(START_DATE)
  const locales = React.useMemo(() => ({ ...defaultLocales, ...localesProp }), [localesProp])
  const formatAdapter = React.useMemo(() => ({ ...defaultFormatAdapter, ...formatAdapterProp }), [formatAdapterProp])

  const context = useDatepicker({
    startDate: value.start,
    endDate: value.end,
    minBookingDate: min,
    maxBookingDate: max,
    focusedInput,
    unavailableDates: disabledDates,
    firstDayOfWeek: formatAdapter.firstDayOfWeek,
    onDatesChange: ({ startDate, endDate, focusedInput }) => {
      setValue({ start: startDate, end: endDate })
      setFocusedInput(focusedInput ?? START_DATE)
    },
  })
  const isStartValid = React.useMemo(() => isValid(value.start), [value.start])
  const isEndValid = React.useMemo(() => isValid(value.end), [value.end])

  const focusStart = React.useCallback(() => setFocusedInput(START_DATE), [])
  const focusEnd = React.useCallback(() => setFocusedInput(END_DATE), [])
  const onClear = React.useCallback(() => {
    setValue({ start: null, end: null })
    focusStart()
  }, [focusStart, setValue])
  const onConfirm = React.useCallback((event) => onConfirmProp(event, value), [onConfirmProp, value])

  return (
    <DatePickerCoreProvider value={{ colorScheme, disabledDates, formatAdapter, ...context }}>
      <Flex zIndex='0' position='relative' marginX='1.5rem' paddingY='1.5rem' {...propsOf.body}>
        <Navigation guide={NAVIGATION_GUIDE.PREV} onClick={context.goToPreviousMonthsByOneMonth} left='0' {...propsOf.prevNavigation} />

        <Stack direction='row' spacing='1.875rem' width='100%' {...propsOf.monthContainer}>
          {context.activeMonths.map((active, index) => (
            <Month key={index} year={active.year} month={active.month} />
          ))}
        </Stack>

        <Navigation guide={NAVIGATION_GUIDE.NEXT} onClick={context.goToNextMonthsByOneMonth} right='0' {...propsOf.nextNavigation} />
      </Flex>

      <Divider margin='0' />

      <Flex padding='1.5rem' {...propsOf.footer}>
        <Stack direction='row' {...propsOf.inputContainer}>
          {withTime ? (
            <Flex>
              <TimePicker
                usePortal={false}
                minWidth='12.5rem'
                height='2.5rem'
                colorScheme={colorScheme}
                placeholder={locales.StartTime}
                formatAdapter={formatAdapter.timePicker}
                value={value.start}
                onChange={(start) => {
                  setValue({ ...value, start })
                  setFocusedInput(START_DATE)
                }}
                isDisabled={!isStartValid}
                listProps={{ minWidth: 'auto', ...propsOf.timePicker?.listProps }}
                buttonProps={{ 'aria-selected': focusedInput === START_DATE, ...propsOf.timePicker?.buttonProps }}
                {...propsOf.timePicker}
              />
            </Flex>
          ) : (
            React.createElement(SelectedDate, {
              ...(focusedInput === START_DATE ? { 'data-focus': true } : {}),
              value: isStartValid ? formatAdapter.input(value.start) : locales.StartDate,
              readOnly: true,
              onClick: focusStart,
              ...propsOf.input,
            })
          )}

          {withTime ? (
            <Flex>
              <TimePicker
                usePortal={false}
                minWidth='12.5rem'
                height='2.5rem'
                colorScheme={colorScheme}
                placeholder={locales.EndTime}
                formatAdapter={formatAdapter.timePicker}
                value={value.end}
                onChange={(end) => {
                  setValue({ ...value, end })
                  setFocusedInput(END_DATE)
                }}
                isDisabled={!isEndValid}
                listProps={{ minWidth: 'auto', ...propsOf.timePicker?.listProps }}
                buttonProps={{ 'aria-selected': focusedInput === END_DATE, ...propsOf.timePicker?.buttonProps }}
                {...propsOf.timePicker}
              />
            </Flex>
          ) : (
            React.createElement(components.input, {
              ...(focusedInput === END_DATE ? { 'data-focus': true } : {}),
              value: isEndValid ? formatAdapter.input(value.end) : locales.EndDate,
              readOnly: true,
              isDisabled: !isStartValid,
              onClick: focusEnd,
              ...propsOf.input,
            })
          )}
        </Stack>

        <Button marginLeft='1rem' variant='link' colorScheme={colorScheme} onClick={onClear} {...propsOf.clear}>
          {locales.Clear}
        </Button>

        <Button marginLeft='auto' colorScheme={colorScheme} onClick={onConfirm} isDisabled={!isStartValid || !isEndValid} {...propsOf.apply}>
          {locales.Apply}
        </Button>
      </Flex>
    </DatePickerCoreProvider>
  )
}

const ValueShape = PropTypes.shape({ start: PropTypes.instanceOf(Date), end: PropTypes.instanceOf(Date) })
DateRangePicker.propTypes = {
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  withTime: PropTypes.bool,
  colorScheme: PropTypes.string,
  disabledDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  defaultValue: ValueShape,
  value: ValueShape,
  onChange: PropTypes.func,
  onConfirm: PropTypes.func,
  formatAdapter: PropTypes.shape({
    firstDayOfWeek: PropTypes.number,
    input: PropTypes.func,
    timePicker: PropTypes.func,
    month: PropTypes.func,
    weekday: PropTypes.func,
    day: PropTypes.func,
  }),
  locales: PropTypes.shape({
    Apply: PropTypes.string,
    Clear: PropTypes.string,
    StartDate: PropTypes.string,
    EndDate: PropTypes.string,
    StartTime: PropTypes.string,
    EndTime: PropTypes.string,
  }),
  components: PropTypes.shape({ input: PropTypes.elementType.isRequired }).isRequired,
  propsOf: PropTypes.shape({
    body: PropTypes.object,
    footer: PropTypes.object,
    prevNavigation: PropTypes.object,
    nextNavigation: PropTypes.object,
    monthContainer: PropTypes.object,
    inputContainer: PropTypes.object,
    timePicker: PropTypes.object,
    input: PropTypes.object,
    clear: PropTypes.object,
    apply: PropTypes.object,
  }).isRequired,
}

DateRangePicker.defaultProps = {
  withTime: false,
  colorScheme: 'brand',
  defaultValue: { start: null, end: null },
  disabledDates: [],
  onConfirm: () => null,
  components: { input: SelectedDate },
  propsOf: {},
}
